import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, Mail, MapPin, Facebook, Instagram, Menu, X as CloseIcon,
} from "lucide-react";
import { PowerBuiltMark } from "@/components/PowerBuiltMark";

/**
 * Shared header + footer used by every public marketing page.
 *
 * Pulled out of LandingPage so /solar, /electrical, /maintenance, /about and
 * /contact all render identical brand chrome without duplicating markup.
 */

const NAV = [
  { label: "Solar",       to: "/solar",        testid: "nav-solar" },
  { label: "Electrical",  to: "/electrical",   testid: "nav-electrical" },
  { label: "Maintenance", to: "/maintenance",  testid: "nav-maintenance" },
  { label: "About",       to: "/about",        testid: "nav-about" },
  { label: "Contact",     to: "/contact",      testid: "nav-contact" },
];

export function BrandWordmark({ compact = false }) {
  return (
    <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
      <PowerBuiltMark className="w-11 h-11 shrink-0 transition-transform group-hover:rotate-3" />
      <div className="leading-tight">
        <div className="font-heading font-semibold tracking-wide text-[15px] text-white">
          PowerBuilt
        </div>
        {!compact && (
          <div className="text-white/50 text-[10px] tracking-[0.28em] uppercase">
            Solar · Electrical · Maintenance
          </div>
        )}
      </div>
    </Link>
  );
}

export function MarketingHeader() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <header className="relative z-20 max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <BrandWordmark />
        <nav className="hidden lg:flex items-center gap-8" data-testid="marketing-nav">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className="text-sm text-white/70 hover:text-white transition"
            >
              {n.label}
            </Link>
          ))}
          <Link
            to="/login"
            data-testid="nav-signin"
            className="text-sm font-medium text-white/80 hover:text-white transition"
          >
            Sign In
          </Link>
          <Link
            to="/register"
            data-testid="nav-getstarted"
            className="btn-accent text-sm px-5 py-2"
          >
            Get Started
          </Link>
        </nav>
        <button
          className="lg:hidden w-10 h-10 grid place-items-center rounded-md text-white hover:bg-white/10"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-nav-toggle"
          aria-label="Toggle menu"
        >
          {open ? <CloseIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-x-0 top-0 z-30 pt-20 bg-[#0A2540]/98 backdrop-blur-md min-h-screen" data-testid="mobile-nav-sheet">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between border-b border-white/10 -mt-16 mb-6">
            <BrandWordmark compact />
            <button
              onClick={() => setOpen(false)}
              className="w-10 h-10 grid place-items-center rounded-md text-white hover:bg-white/10"
              aria-label="Close menu"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="max-w-6xl mx-auto px-6 flex flex-col gap-3">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="text-lg py-3 border-b border-white/10 text-white/85 hover:text-white"
                onClick={() => setOpen(false)}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/login"
              className="text-lg py-3 border-b border-white/10 text-white/85 hover:text-white"
              onClick={() => setOpen(false)}
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="btn-accent text-base mt-4 py-3 text-center"
              onClick={() => setOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

export function MarketingFooter() {
  return (
    <footer className="relative z-10 border-t border-white/10 mt-12" data-testid="marketing-footer">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div className="md:col-span-2">
            <div className="mb-4"><BrandWordmark /></div>
            <p className="text-sm text-white/60 leading-relaxed max-w-md">
              Solar, electrical and maintenance services for Sint Maarten. Built to last, backed by service — one project at a time.
            </p>
          </div>
          <div>
            <p className="overline text-[#C9A356] mb-3">Services</p>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/solar"       className="hover:text-white transition">Solar &amp; battery storage</Link></li>
              <li><Link to="/electrical"  className="hover:text-white transition">Electrical work</Link></li>
              <li><Link to="/maintenance" className="hover:text-white transition">System maintenance</Link></li>
              <li><Link to="/impact"      className="hover:text-white transition">Our impact</Link></li>
            </ul>
          </div>
          <div>
            <p className="overline text-[#C9A356] mb-3">Contact</p>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#C9A356] mt-0.5 shrink-0" />
                <span>Cole Bay, Sint Maarten</span>
              </li>
              <li>
                <a href="mailto:hello@powerbuilt.sx" className="flex items-start gap-2 hover:text-white transition" data-testid="footer-email">
                  <Mail className="w-4 h-4 text-[#C9A356] mt-0.5 shrink-0" />
                  <span>hello@powerbuilt.sx</span>
                </a>
              </li>
              <li>
                <Link to="/contact" className="flex items-start gap-2 hover:text-white transition" data-testid="footer-contact">
                  <Phone className="w-4 h-4 text-[#C9A356] mt-0.5 shrink-0" />
                  <span>Request a quote</span>
                </Link>
              </li>
            </ul>
            <div className="flex gap-2 mt-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer"
                 className="w-9 h-9 rounded-md border border-white/20 grid place-items-center text-white/70 hover:text-white hover:border-[#C9A356] transition"
                 data-testid="footer-facebook" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"
                 className="w-9 h-9 rounded-md border border-white/20 grid place-items-center text-white/70 hover:text-white hover:border-[#C9A356] transition"
                 data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-white/50">
          <div>© 2026 PowerBuilt · Solar, Electrical &amp; Maintenance</div>
          <div className="flex gap-4">
            <Link to="/impact"  className="hover:text-white transition">Our Impact</Link>
            <Link to="/about"   className="hover:text-white transition">About</Link>
            <Link to="/contact" className="hover:text-white transition">Contact</Link>
            <Link to="/login"   className="hover:text-white transition">Sign In</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/** Standard dark backdrop used across marketing pages. */
export function MarketingBackdrop({ children }) {
  return (
    <div className="min-h-screen bg-[#0A2540] text-white relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#C9A356]/12 blur-3xl pointer-events-none"></div>
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-[#C9A356]/8 blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-40 left-1/4 w-[420px] h-[420px] rounded-full bg-[#8CDC5A]/8 blur-3xl pointer-events-none"></div>
      {children}
    </div>
  );
}
